import { Fragment, useEffect, useMemo, useState } from "react";
import API_BASE from "../../config";
import {
  averageOf, fetchGradingPeriods, periodsForOffering, withLegacyPeriods,
} from "../../utils/gradingPeriods";
import s from "./Grades.module.css";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

/** Loads grades for one student plus everything needed to render them. */
function useStudentGrades(studentId) {
  const EMPTY = { loading: false, error: "", grades: [], offerings: {}, subjects: {}, semesters: {},
                  sectionList: [], gradeLevelList: [], semesterList: [], periodRows: [] };
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    if (!studentId) {
      setState(EMPTY);
      return;
    }
    let cancelled = false;
    setState(p => ({ ...p, loading: true, error: "" }));
    (async () => {
      try {
        const soft = (u) => fetch(u).then(r => (r.ok ? r.json() : [])).catch(() => []);
        const [grades, offerings, subjects, semesters, sectionList, gradeLevelList, periodRows] = await Promise.all([
          fetch(`${API_BASE}/api/grades?studentId=${studentId}`).then(r => r.json()),
          fetch(`${API_BASE}/api/class-offerings`).then(r => r.json()),
          fetch(`${API_BASE}/api/subjects`).then(r => r.json()),
          fetch(`${API_BASE}/api/semesters`).then(r => r.json()),
          // Needed to know each class's grading periods (quarters vs
          // midterm/final) — section → grade level → Level.
          soft(`${API_BASE}/api/sections`),
          soft(`${API_BASE}/api/grade-levels`),
          fetchGradingPeriods(),
        ]);
        if (cancelled) return;
        setState({
          loading: false,
          error: "",
          grades,
          offerings: Object.fromEntries(offerings.map(r => [r.id, r])),
          subjects: Object.fromEntries(subjects.map(r => [r.id, r])),
          semesters: Object.fromEntries(semesters.map(r => [r.id, r])),
          sectionList, gradeLevelList, semesterList: semesters, periodRows,
        });
      } catch (e) {
        if (!cancelled) setState(p => ({ ...p, loading: false, error: e.message }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  return state;
}

/** Loads the parent's linked children. */
function useParentChildren(parentId) {
  const [state, setState] = useState({ loading: false, children: [] });
  useEffect(() => {
    if (!parentId) return;
    let cancelled = false;
    (async () => {
      try {
        const links = await fetch(`${API_BASE}/api/parent-student?parentId=${parentId}`).then(r => r.json());
        const children = await Promise.all(
          links.map(l => fetch(`${API_BASE}/api/students/${l.studentId}`).then(r => r.json()))
        );
        if (!cancelled) setState({ loading: false, children });
      } catch {
        if (!cancelled) setState({ loading: false, children: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [parentId]);
  return state;
}

// ── Pure computations (no side effects) ────────────────────────────────────

/** Numeric score from a grade row, or null if blank. Score is BigDecimal on
 *  the server so it can deserialize as a string or number — handle both. */
function num(g) {
  if (!g || g.score == null) return null;
  const n = Number(g.score);
  return Number.isFinite(n) ? n : null;
}

/** Subject average: mean of whichever grading periods have scores (so a
 *  subject with only the 1st quarter encoded still shows a value). */
function subjectAverage(cell, codes) {
  return averageOf(cell, codes);
}

/** Map a 0-100 numeric score to a short letter / level label. Follows the PH
 *  DepEd descriptor convention used elsewhere in this app. */
function letterFor(score) {
  if (score == null) return { label: "—", tone: "none" };
  if (score >= 90) return { label: "Outstanding",         tone: "high" };
  if (score >= 85) return { label: "Very Satisfactory",   tone: "high" };
  if (score >= 80) return { label: "Satisfactory",        tone: "high" };
  if (score >= 75) return { label: "Fairly Satisfactory", tone: "mid"  };
  return                  { label: "Needs Improvement",   tone: "low"  };
}

/** Honor descriptor based on a numeric GPA. DepEd honor brackets. */
function honorFor(gpa) {
  if (gpa == null) return null;
  if (gpa >= 98) return { label: "With Highest Honors", tier: 3 };
  if (gpa >= 95) return { label: "With High Honors",    tier: 2 };
  if (gpa >= 90) return { label: "With Honors",         tier: 1 };
  return null;
}

/** Stable hue from a subject code/title — gives each subject a consistent
 *  accent across cards without needing a colour column in the DB. */
function subjectHue(seed) {
  const str = String(seed || "•");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 360;
}

/** Two-letter abbreviation for the subject icon. */
function subjectAbbrev(subj, subjId) {
  const src = (subj?.code || subj?.title || `S${subjId}`).toString();
  // Try first letters of first two words, falling back to first two chars.
  const words = src.replace(/[^A-Za-z0-9 ]/g, " ").split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return src.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "S";
}

/** Round to one decimal for display. */
function fmt1(v) {
  if (v == null || !Number.isFinite(v)) return "—";
  return (Math.round(v * 10) / 10).toFixed(1);
}

export default function GradesPage() {
  const user = getUser();
  const role = user?.role;

  // Parent: pick a child first
  const { children } = useParentChildren(role === "Parent" ? user?.linkedEntity?.id : null);
  const [pickedChildId, setPickedChildId] = useState(null);

  useEffect(() => {
    if (role === "Parent" && children.length > 0 && pickedChildId == null) {
      setPickedChildId(children[0].id);
    }
  }, [role, children, pickedChildId]);

  const studentId =
    role === "Student" ? user?.linkedEntity?.id :
    role === "Parent"  ? pickedChildId :
    null;

  const {
    loading, error, grades, offerings, subjects, semesters,
    sectionList, gradeLevelList, semesterList, periodRows,
  } = useStudentGrades(studentId);

  // Group grades by semester → subject and pre-compute everything the UI needs.
  // Doing the math here once means the render path is just lookup + map; keeps
  // re-renders cheap when only `selectedSemId` changes.
  const summary = useMemo(() => {
    // groups[semId] = { semId, bySubj: { [subjId]: { offering, [periodCode]?: grade } } }
    const groups = {};
    grades.forEach(g => {
      const off = offerings[g.classOfferingId];
      if (!off) return;
      const semId = off.semesterId;
      const subjId = off.subjectId;
      if (!groups[semId]) groups[semId] = { semId, bySubj: {} };
      if (!groups[semId].bySubj[subjId]) groups[semId].bySubj[subjId] = { offering: off };
      groups[semId].bySubj[subjId][g.period] = g;
    });

    // Per-semester aggregate: subject averages, GPA, top subject.
    const perSemester = Object.values(groups).map(grp => {
      const subjects = Object.entries(grp.bySubj).map(([subjId, cell]) => {
        // This class's periods (1st/2nd Quarter, Midterm/Final…) plus any
        // older codes that already hold a grade.
        const periods = withLegacyPeriods(
          periodsForOffering({
            offering: cell.offering, sections: sectionList, gradeLevels: gradeLevelList,
            semesters: semesterList, rows: periodRows,
          }),
          Object.values(cell).filter(v => v && v.period)
        );
        const avg = subjectAverage(cell, periods.map(x => x.code));
        return {
          subjId: Number(subjId),
          cell,
          periods,
          avg,
          letter: letterFor(avg),
        };
      });
      const withAvg = subjects.filter(x => x.avg != null);
      const gpa = withAvg.length === 0
        ? null
        : withAvg.reduce((a, x) => a + x.avg, 0) / withAvg.length;
      const top = withAvg.length === 0
        ? null
        : withAvg.reduce((best, x) => (best == null || x.avg > best.avg ? x : best), null);
      return { semId: grp.semId, subjects, gpa, top, count: subjects.length };
    });

    // Sort semesters by their label using the semester map. Falls back to id.
    perSemester.sort((a, b) => {
      const la = semesters[a.semId]?.label || "";
      const lb = semesters[b.semId]?.label || "";
      if (la && lb) return String(la).localeCompare(String(lb));
      return a.semId - b.semId;
    });

    // Overall GPA = mean of per-semester GPAs that have any subjects.
    const semGpas = perSemester.map(p => p.gpa).filter(v => v != null);
    const overall = semGpas.length === 0 ? null : semGpas.reduce((a, b) => a + b, 0) / semGpas.length;

    return { perSemester, overall };
  }, [grades, offerings, semesters, sectionList, gradeLevelList, semesterList, periodRows]);

  // Selected semester — defaults to the latest one with data.
  const [selectedSemId, setSelectedSemId] = useState(null);
  useEffect(() => {
    if (summary.perSemester.length === 0) { setSelectedSemId(null); return; }
    const hasCurrent = summary.perSemester.some(p => p.semId === selectedSemId);
    if (!hasCurrent) {
      setSelectedSemId(summary.perSemester[summary.perSemester.length - 1].semId);
    }
  }, [summary.perSemester, selectedSemId]);

  const selectedSummary = useMemo(
    () => summary.perSemester.find(p => p.semId === selectedSemId) || null,
    [summary.perSemester, selectedSemId]
  );

  const honor    = honorFor(selectedSummary?.gpa);
  const overallHonor = honorFor(summary.overall);

  const childName = (() => {
    if (role !== "Parent") return null;
    const c = children.find(c => c.id === pickedChildId);
    return c ? `${c.firstName || ""} ${c.lastName || ""}`.trim() : null;
  })();

  return (
    <div className={s.page}>
      {/* ── Premium hero ────────────────────────────────────────────── */}
      <section className={s.gbHero}>
        <div className={s.gbHeroIcon} aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
            <path d="M6 12v5c3 3 9 3 12 0v-5"/>
          </svg>
        </div>
        <div className={s.gbHeroBody}>
          <div className={s.eyebrow}>
            {role === "Parent" ? "Parent" : "Student"} · Academic Record
          </div>
          <h1 className={s.gbHeroTitle}>
            {childName ? `${childName}'s Grades` : "Academic Record"}
          </h1>
          <p className={s.gbHeroSub}>
            {role === "Parent"
              ? "Live academic snapshot for your linked children. Switch between children below; GPA, honor status, and per-subject trends update instantly."
              : "Your live academic snapshot — semester GPAs, honor status, per-subject averages, and term-over-term trend. Read-only."}
          </p>
        </div>
        {overallHonor && (
          <div className={`${s.gbHonorRibbon} ${s["gbHonorTier" + overallHonor.tier]}`}
               title={`Overall ${overallHonor.label.toLowerCase()}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="6"/>
              <path d="M8.21 13.89 7 22l5-3 5 3-1.21-8.12"/>
            </svg>
            <span>{overallHonor.label}</span>
          </div>
        )}
      </section>

      {role === "Parent" && children.length === 0 && (
        <div className={s.card}><div className={s.empty}>No linked children. Ask an admin to link you.</div></div>
      )}

      {role === "Parent" && children.length > 0 && (
        <div className={s.childPicker}>
          {children.map(c => (
            <button
              key={c.id}
              type="button"
              className={`${s.childBtn} ${pickedChildId === c.id ? s.childBtnActive : ""}`}
              onClick={() => setPickedChildId(c.id)}>
              {c.firstName} {c.lastName}
            </button>
          ))}
        </div>
      )}

      {!studentId && role !== "Parent" && (
        <div className={s.card}>
          <div className={s.empty}>Your account isn't linked to a Student record yet.</div>
        </div>
      )}

      {studentId && loading && (
        <div className={s.card}>
          <div className={s.skeleton}>
            <div className={s.skeletonRow} />
            <div className={s.skeletonRow} />
            <div className={s.skeletonRow} />
          </div>
        </div>
      )}

      {studentId && error && (
        <div className={s.card}><div className={s.error}>{error}</div></div>
      )}

      {studentId && !loading && !error && summary.perSemester.length === 0 && (
        <div className={s.card}>
          <div className={s.emptyPremium}>
            <div className={s.emptyPremiumTitle}>No grades on record yet</div>
            <div className={s.emptyPremiumSub}>
              Once your teachers encode grades for this term, they'll appear here.
            </div>
          </div>
        </div>
      )}

      {/* ── Stat tiles ─────────────────────────────────────────────── */}
      {studentId && !loading && !error && summary.perSemester.length > 0 && (
        <>
          <div className={s.gbStatGrid}>
            <StatTile
              label="Overall GPA"
              value={fmt1(summary.overall)}
              hint={overallHonor ? overallHonor.label : "Across every term on file"}
              tone={summary.overall == null ? "none" : letterFor(summary.overall).tone}
            />
            <StatTile
              label={selectedSummary ? "Term GPA" : "Term GPA"}
              value={fmt1(selectedSummary?.gpa)}
              hint={honor
                ? honor.label
                : (selectedSummary
                    ? `${selectedSummary.count} subject${selectedSummary.count === 1 ? "" : "s"}`
                    : "—")}
              tone={selectedSummary?.gpa == null ? "none" : letterFor(selectedSummary.gpa).tone}
            />
            <StatTile
              label="Top Subject"
              value={selectedSummary?.top
                ? fmt1(selectedSummary.top.avg)
                : "—"}
              hint={selectedSummary?.top
                ? (subjects[selectedSummary.top.subjId]?.title
                    || subjects[selectedSummary.top.subjId]?.code
                    || `Subject #${selectedSummary.top.subjId}`)
                : "Highest subject this term"}
              tone="high"
            />
            <StatTile
              label="Terms Tracked"
              value={String(summary.perSemester.length)}
              hint="Semesters with encoded grades"
              tone="none"
            />
          </div>

          {/* ── Semester tabs ──────────────────────────────────────── */}
          {summary.perSemester.length > 1 && (
            <div className={s.gbSemTabs} role="tablist" aria-label="Semesters">
              {summary.perSemester.map(p => {
                const sem = semesters[p.semId];
                const label = sem ? `${sem.label} Sem` : `Semester #${p.semId}`;
                const active = p.semId === selectedSemId;
                return (
                  <button
                    key={p.semId}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`${s.gbSemTab} ${active ? s.gbSemTabActive : ""}`}
                    onClick={() => setSelectedSemId(p.semId)}>
                    <span className={s.gbSemTabLabel}>{label}</span>
                    <span className={s.gbSemTabValue}>{fmt1(p.gpa)}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Selected semester · subject cards ─────────────────── */}
          {selectedSummary && (
            <section className={s.gbTermPanel} aria-labelledby="gbTermHeading">
              <header className={s.gbTermHead}>
                <div>
                  <h2 id="gbTermHeading" className={s.gbTermTitle}>
                    {(() => {
                      const sem = semesters[selectedSummary.semId];
                      return sem ? `${sem.label} Semester` : `Semester #${selectedSummary.semId}`;
                    })()}
                  </h2>
                  <div className={s.gbTermSub}>
                    {selectedSummary.count} subject{selectedSummary.count === 1 ? "" : "s"} ·{" "}
                    GPA {fmt1(selectedSummary.gpa)}
                  </div>
                </div>
                {honor && (
                  <span className={`${s.gbHonorPill} ${s["gbHonorTier" + honor.tier]}`}>
                    {honor.label}
                  </span>
                )}
              </header>

              <div className={s.gbSubjectGrid}>
                {selectedSummary.subjects.map((row, idx) => {
                  const subj = subjects[row.subjId];
                  const hue  = subjectHue(subj?.code || subj?.title || row.subjId);
                  const abbr = subjectAbbrev(subj, row.subjId);
                  const remarks = row.periods.map(pp => row.cell[pp.code]?.remarks)
                    .filter(Boolean).join(" · ");

                  return (
                    <article
                      key={row.subjId}
                      className={`${s.gbSubjectCard} ${s["gbTone_" + row.letter.tone]}`}
                      style={{ "--gbHue": hue, animationDelay: `${Math.min(idx, 8) * 35}ms` }}>
                      <header className={s.gbSubjectHead}>
                        <div className={s.gbSubjectIcon} aria-hidden="true">{abbr}</div>
                        <div className={s.gbSubjectIdent}>
                          <div className={s.gbSubjectName}
                               title={subj?.title || `Subject #${row.subjId}`}>
                            {subj?.title || `Subject #${row.subjId}`}
                          </div>
                          {subj?.code && (
                            <div className={s.gbSubjectCode}>{subj.code}</div>
                          )}
                        </div>
                        <div className={s.gbSubjectAvg}>
                          <div className={s.gbSubjectAvgValue}>{fmt1(row.avg)}</div>
                          <div className={s.gbSubjectAvgLabel}>{row.letter.label}</div>
                        </div>
                      </header>

                      <div className={s.gbPeriodRow}>
                        {row.periods.map((pp, i) => (
                          <Fragment key={pp.code}>
                            {i > 0 && <div className={s.gbPeriodDivider} aria-hidden="true" />}
                            <PeriodScore label={pp.label} score={num(row.cell[pp.code])} />
                          </Fragment>
                        ))}
                      </div>

                      {/* Progress meter — 0 to 100 scale visualization. */}
                      <div className={s.gbMeter} aria-hidden="true">
                        <div
                          className={s.gbMeterFill}
                          style={{ width: `${Math.max(0, Math.min(100, row.avg ?? 0))}%` }}
                        />
                        <div className={s.gbMeterTick} style={{ left: "75%" }} title="Passing" />
                      </div>

                      {remarks && (
                        <div className={s.gbRemarks} title={remarks}>
                          <span className={s.gbRemarksLabel}>Note</span>
                          <span className={s.gbRemarksText}>{remarks}</span>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {/* ── Trend strip (only when ≥ 2 semesters have data) ───── */}
          {summary.perSemester.length >= 2 && (
            <section className={s.gbTrend}>
              <header className={s.gbTrendHead}>
                <div>
                  <h2 className={s.gbTrendTitle}>Term-over-term trend</h2>
                  <p className={s.gbTrendSub}>
                    Semester GPAs across every term on file. Bar fill scales 60 → 100 so
                    differences are easy to read.
                  </p>
                </div>
              </header>
              <div className={s.gbTrendBars}>
                {summary.perSemester.map((p, i) => {
                  const prev = i > 0 ? summary.perSemester[i - 1].gpa : null;
                  const delta = (p.gpa != null && prev != null) ? p.gpa - prev : null;
                  const sem = semesters[p.semId];
                  const label = sem ? `${sem.label} Sem` : `Semester #${p.semId}`;
                  // Scale 60→100 to bar 6%→100% so a 78 vs 92 visibly differ.
                  const pct = p.gpa == null ? 0 :
                    Math.max(6, Math.min(100, ((p.gpa - 60) / 40) * 100));
                  return (
                    <div key={p.semId} className={s.gbTrendItem}>
                      <div className={s.gbTrendBarWrap}>
                        <div
                          className={`${s.gbTrendBar} ${s["gbTone_" + letterFor(p.gpa).tone]}`}
                          style={{ height: `${pct}%`, animationDelay: `${i * 60}ms` }}
                          aria-label={`${label}: GPA ${fmt1(p.gpa)}`}
                        />
                      </div>
                      <div className={s.gbTrendValue}>{fmt1(p.gpa)}</div>
                      <div className={s.gbTrendLabel}>{label}</div>
                      {delta != null && (
                        <div className={`${s.gbTrendDelta} ${
                          delta > 0.05 ? s.gbDeltaUp
                          : delta < -0.05 ? s.gbDeltaDown
                          : s.gbDeltaFlat}`}>
                          {delta > 0.05 ? "▲" : delta < -0.05 ? "▼" : "•"}{" "}
                          {Math.abs(delta) < 0.05 ? "0.0" : fmt1(Math.abs(delta))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ── Small pure presentational pieces ────────────────────────────────────────

function StatTile({ label, value, hint, tone }) {
  return (
    <div className={`${s.gbStatTile} ${s["gbTone_" + (tone || "none")]}`}>
      <div className={s.gbStatLabel}>{label}</div>
      <div className={s.gbStatValue}>{value ?? "—"}</div>
      <div className={s.gbStatHint}>{hint}</div>
    </div>
  );
}

function PeriodScore({ label, score }) {
  const tone = letterFor(score).tone;
  return (
    <div className={`${s.gbPeriod} ${s["gbTone_" + tone]}`}>
      <div className={s.gbPeriodLabel}>{label}</div>
      <div className={s.gbPeriodValue}>
        {score == null ? "—" : (Math.round(score * 10) / 10).toFixed(1)}
      </div>
    </div>
  );
}
